import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BookOpen,
  CalendarDays,
  Gift,
  LayoutDashboard,
  LogOut,
  Menu,
  Sparkles,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ErrorState, Loading } from "@/components/States";
import { ParentContext } from "@/context/ParentContext";
import BrandLogo from "@/components/BrandLogo";

const NAV = [
  { to: "/p", label: "Panel", icon: LayoutDashboard, end: true, testId: "nav-parent-dashboard", tone: "bg-violet-100 text-violet-700" },
  { to: "/p/estudiantes", label: "Hijos", icon: Users, testId: "nav-parent-students", tone: "bg-cyan-100 text-cyan-700" },
  { to: "/p/tutores", label: "Tutores", icon: UserPlus, testId: "nav-parent-tutors", tone: "bg-lime-100 text-emerald-700" },
  { to: "/p/material", label: "Material", icon: BookOpen, testId: "nav-parent-material", tone: "bg-amber-100 text-amber-700" },
  { to: "/p/calendario", label: "Examenes", icon: CalendarDays, testId: "nav-parent-exams", tone: "bg-pink-100 text-pink-700" },
  { to: "/p/recompensas", label: "Recompensas", icon: Gift, testId: "nav-parent-rewards", tone: "bg-orange-100 text-orange-700" },
  { to: "/p/asistente", label: "Asistente IA", icon: Sparkles, testId: "nav-parent-assistant", tone: "bg-fuchsia-100 text-fuchsia-700" },
];

const QUICK_NAV = [NAV[0], NAV[1], NAV[3], NAV[4], NAV[6]];

export default function ParentShell() {
  const { principal, logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState(null);
  const [selectedId, setSelectedId] = useState(localStorage.getItem("estudia_selected_student") || "");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/parent/students");
      setStudents(data);
      if (data.length && !data.some((s) => s.id === selectedId)) {
        setSelectedId(data[0].id);
        localStorage.setItem("estudia_selected_student", data[0].id);
      }
    } catch (err) {
      setError(apiError(err, "No pudimos cargar tus hijos."));
      setStudents([]);
    }
  }, [selectedId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectStudent = (id) => {
    setSelectedId(id);
    localStorage.setItem("estudia_selected_student", id);
  };

  const student = students?.find((s) => s.id === selectedId) || null;

  return (
    <ParentContext.Provider value={{ students: students || [], student, selectedId, selectStudent, reloadStudents: load }}>
      <div className="min-h-screen bg-gradient-to-b from-[#faf7ff] via-white to-[#f2fbff] pb-28">
        <header className="sticky top-0 z-30 border-b-2 border-violet-100 bg-white/88 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
            <BrandLogo to="/p" className="shrink-0" imageClassName="h-16 w-auto sm:h-20" data-testid="parent-logo" />

            <div className="ml-auto flex items-center gap-2">
              {students?.length > 1 && (
                <select
                  value={selectedId}
                  onChange={(e) => selectStudent(e.target.value)}
                  className="estudia-touch rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium"
                  data-testid="student-switcher"
                >
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.first_name}
                    </option>
                  ))}
                </select>
              )}
              <span className="hidden text-sm text-slate-500 md:inline" data-testid="parent-name">
                {principal?.name}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="hidden md:inline-flex"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                data-testid="parent-logout-btn"
                aria-label="Cerrar sesion"
              >
                <LogOut className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setOpen((v) => !v)}
                data-testid="parent-menu-toggle"
                aria-label="Menu"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {open && (
            <nav className="border-t border-slate-200 bg-white px-4 py-2 lg:hidden" aria-label="Menú familiar móvil" data-testid="parent-mobile-nav">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  data-testid={`${item.testId}-mobile`}
                  className={({ isActive }) =>
                    `flex estudia-touch items-center gap-3 rounded-2xl px-3 text-sm font-bold ${
                      isActive ? "bg-violet-600 text-white" : "text-slate-600"
                    }`
                  }
                >
                  <span className={`grid h-9 w-9 place-items-center rounded-xl ${item.tone}`}><item.icon className="h-5 w-5" /></span>
                  {item.label}
                </NavLink>
              ))}
              <button
                className="flex estudia-touch w-full items-center gap-3 rounded-xl px-3 text-sm font-semibold text-slate-600"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                data-testid="parent-logout-btn-mobile"
              >
                <LogOut className="h-4 w-4" /> Cerrar sesion
              </button>
            </nav>
          )}
        </header>

        <div className="mx-auto flex max-w-7xl gap-8 px-4 py-6 sm:px-6 lg:py-10">
          <aside className="hidden w-56 shrink-0 lg:block">
            <nav className="sticky top-24 space-y-1" aria-label="Navegación familiar principal">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex estudia-touch items-center gap-3 rounded-2xl px-3 text-sm font-bold transition-all ${
                      isActive ? "play-button bg-slate-900 text-white" : "text-slate-600 hover:-translate-y-0.5 hover:bg-white hover:shadow-sm"
                    }`
                  }
                >
                  <span className={`grid h-10 w-10 place-items-center rounded-xl ${item.tone}`}><item.icon className="h-5 w-5" /></span>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </aside>

          <main className="min-w-0 flex-1">
            {error ? (
              <ErrorState message={error} onRetry={load} />
            ) : students === null ? (
              <Loading label="Cargando tu familia..." />
            ) : (
              <Outlet />
            )}
          </main>
        </div>

        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-violet-100 bg-white/95 px-2 shadow-[0_-12px_36px_rgba(65,70,130,0.16)] backdrop-blur-xl"
          aria-label="Accesos rápidos familiares"
          data-testid="parent-bottom-nav"
        >
          <div className="mx-auto flex max-w-3xl">
            {QUICK_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`${item.testId}-bottom`}
                className={({ isActive }) =>
                  `group my-2 flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-1 py-1.5 text-[11px] font-extrabold transition sm:text-xs ${
                    isActive ? "-translate-y-1 bg-violet-100 text-violet-700 shadow-sm" : "text-slate-500 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-violet-700"
                  }`
                }
              >
                <span className={`grid h-10 w-10 place-items-center rounded-2xl shadow-sm transition-transform group-hover:scale-105 ${item.tone}`}>
                  <item.icon className="h-6 w-6" />
                </span>
                <span className="max-w-full truncate">{item.label === "Asistente IA" ? "Asistente" : item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </ParentContext.Provider>
  );
}
